import { inject } from '@angular/core';
import { NonNullableFormBuilder, Validators } from '@angular/forms';

import { QuestionService } from '@core/ask-hr-dynamic-forms-smart-ui';
import type {
  DefineFormData,
  DynamicFormConfig,
  RespondFormWrapperConfig,
  UnwrapSignal,
} from '@core/ask-hr-dynamic-forms-types';
import type { BooleanTemplateItem } from '@core/ask-hr-templates-types';
import { I18nService } from '@core/i18n-legacy';

interface RespondConfig {
  question: string;
  hint: string;
  required: boolean;
  options: Array<{ label: string; value: 'true' | 'false' }>;
}

type RespondComponentType =
  import('@core/ask-hr-dynamic-forms-smart-ui').RespondSelectSingleComponent<
    RespondConfig['options'][number]['value']
  >;

export class BooleanConfig implements DynamicFormConfig {
  private readonly formBuilder = inject(NonNullableFormBuilder);
  private readonly questionService = inject(QuestionService);
  private readonly i18n = inject(I18nService);

  public readonly kind: BooleanTemplateItem['type'] = 'boolean';
  public readonly defineConfig: DynamicFormConfig['defineConfig'] = {
    name: this.i18n.translate('tlc.ask_hr.form_template_create.configs.boolean'),
    icon: 'radio-button-checked',
  };

  public readonly components = {
    define: () => import('@core/ask-hr-dynamic-forms-smart-ui').then(m => m.QuestionComponent),
    respond: () =>
      import('@core/ask-hr-dynamic-forms-smart-ui').then(m => m.RespondSelectSingleComponent),
    present: () =>
      import('@core/ask-hr-dynamic-forms-smart-ui').then(m => m.TextPresenterComponent),
  };

  public readonly defineForm = (
    data?: DefineFormData,
  ): UnwrapSignal<import('@core/ask-hr-dynamic-forms-smart-ui').QuestionComponent['defineForm']> =>
    this.questionService.createQuestionForm(data);

  public readonly respondConfig = (
    data: RespondFormWrapperConfig,
  ): UnwrapSignal<RespondComponentType['respondConfig']> => ({
    ...data,
    options: [
      {
        label: this.i18n.translate('tlc.ask_hr.form_template_create.configs.boolean_yes'),
        value: 'true',
      },
      {
        label: this.i18n.translate('tlc.ask_hr.form_template_create.configs.boolean_no'),
        value: 'false',
      },
    ],
    kind: 'radio',
  });

  public readonly respondForm = (config: {
    required: boolean;
  }): UnwrapSignal<RespondComponentType['respondForm']> =>
    this.formBuilder.group({
      value: this.formBuilder.control<'true' | 'false' | null>(
        null,
        config.required ? Validators.required : [],
      ),
    });

  public presentConfig = (
    data: BooleanTemplateItem,
  ): UnwrapSignal<
    import('@core/ask-hr-dynamic-forms-smart-ui').TextPresenterComponent['config']
  > => ({
    heading: data.name,
    value:
      data.value === null
        ? '-'
        : {
            true: this.i18n.translate('tlc.ask_hr.form_template_create.configs.boolean_yes'),
            false: this.i18n.translate('tlc.ask_hr.form_template_create.configs.boolean_no'),
          }[data.value ? 'true' : 'false'],
    kind: {
      type: 'text',
      multiline: false,
    },
  });
}
